// Policy flow — a chart of what the active policy *does*, built by evaluating
// it rather than by reading it.
//
// ## Why not parse the Rego
//
// The visual views the console had could only ever render a policy the console
// itself had authored: they read the `@vtc-rule-ir` header, and a hand-written
// module carries none. Writing a Rego parser would cover the subset we happened
// to think of, and a partial parser that quietly mis-draws a policy is worse
// than no picture at all — an operator would trust it.
//
// So this treats a policy as what it is: a function from verified Facts to a
// verdict. The ceremony manifest already declares the inputs its dry-run can
// vary, so the combinations of those inputs are a finite space. Evaluate the
// policy at every point of it (`POST /v1/policies/{id}/test`, the daemon's own
// evaluator) and the result is a decision table, which induces a flow chart.
//
// That renders **any** policy, and it is also more truthful than the authored
// view for the policies that do carry an IR: the header is a claim its author
// maintains by hand, and the shipped `join.rego` has already drifted from its
// own — the header says the invitation route admits as `member`, the body
// admits at `invited_role` from the invitation's scopes. A probe shows what
// runs.
//
// ## What it cannot do
//
// The chart is exactly as complete as the inputs the ceremony declares. A
// policy reading a fact no field varies is invisible here, and a chart that
// hid that would be the same lie as the partial parser. So [`probeSpace`]
// refuses rather than truncates, and the surface states its own bounds.

import type {
  CeremonyManifest,
  FieldDef,
  FieldValues,
} from "@/lib/ceremony-manifest";
import { evalShowWhen } from "@/lib/ceremony-manifest";

/** A decision, as the four-valued pipeline returns it. */
export interface Verdict {
  effect: string;
  with?: Record<string, unknown>;
}

/** One evaluation: the inputs, and what the policy said about them. */
export interface ProbeRow {
  values: FieldValues;
  verdict: Verdict | null;
}

/** A question the chart asks, and where each answer leads. */
export interface FlowAsk {
  kind: "ask";
  /** The manifest field this question is about. */
  field: string;
  label: string;
  hint?: string;
  branches: FlowBranch[];
}

export interface FlowBranch {
  value: string | boolean;
  label: string;
  node: FlowNode;
}

/** An ending: every probe below this point decided the same way. */
export interface FlowVerdict {
  kind: "verdict";
  effect: string;
  with?: Record<string, unknown>;
}

/**
 * Reached when the probes disagree and no declared input separates them —
 * the policy is reading something the ceremony does not expose. Naming it is
 * the point: a chart that quietly averaged over it would be misleading.
 */
export interface FlowVaries {
  kind: "varies";
  effects: string[];
}

export type FlowNode = FlowAsk | FlowVerdict | FlowVaries;

/** How many evaluations the console will ask the daemon for. */
export const PROBE_LIMIT = 96;

/** The values a field can take in a probe. */
function optionsFor(field: FieldDef): (string | boolean)[] {
  if (field.type === "toggle") return [false, true];
  const values = field.options?.map((o) => o.value) ?? [];
  return values.length > 0 ? values : [field.default];
}

function labelFor(field: FieldDef, value: string | boolean): string {
  if (field.type === "toggle") return value ? "yes" : "no";
  return field.options?.find((o) => o.value === value)?.label ?? String(value);
}

/**
 * Every combination of the ceremony's inputs, or `null` when there are more
 * than `limit` of them.
 *
 * A field whose `showWhen` is false for a combination is not varied within it
 * — it takes its default, exactly as the dry-run would send it. That is not
 * only cheaper: varying a control the ceremony hides would probe states an
 * operator cannot reach, and put questions in the chart they cannot answer.
 */
export function probeSpace(
  ceremony: CeremonyManifest,
  limit = PROBE_LIMIT,
): FieldValues[] | null {
  let rows: FieldValues[] = [{}];
  for (const field of ceremony.fields) {
    const next: FieldValues[] = [];
    for (const row of rows) {
      if (!evalShowWhen(field.showWhen, row)) {
        next.push({ ...row, [field.key]: field.default });
        continue;
      }
      for (const value of optionsFor(field)) {
        next.push({ ...row, [field.key]: value });
      }
    }
    if (next.length > limit) return null;
    rows = next;
  }
  return rows;
}

/** Two verdicts are the same ending when both the effect and its `with` match. */
export function verdictKey(verdict: Verdict | null): string {
  if (!verdict) return " none";
  return `${verdict.effect}|${JSON.stringify(verdict.with ?? {})}`;
}

interface Scored {
  field: FieldDef;
  groups: Map<string, ProbeRow[]>;
  /**
   * Endings still left after the split, weighted by how much of the space each
   * branch covers. Weighting is what makes wide and narrow splits comparable:
   * summing the branches unweighted penalises a five-way question for having
   * five branches, and the chart then leads with a toggle that settles almost
   * nothing.
   */
  impurity: number;
}

function group(rows: ProbeRow[], field: FieldDef): Map<string, ProbeRow[]> {
  const groups = new Map<string, ProbeRow[]>();
  for (const row of rows) {
    const key = JSON.stringify(row.values[field.key]);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  return groups;
}

function distinctEndings(rows: ProbeRow[]): Set<string> {
  return new Set(rows.map((r) => verdictKey(r.verdict)));
}

/**
 * Build the chart from a decision table.
 *
 * At each step it asks the input that separates the endings best — fewest
 * distinct endings left across the branches, then fewest branches, then the
 * ceremony's own field order, so the chart reads in the order the form does.
 * A question whose every answer leads to the same ending is dropped, which is
 * what keeps an irrelevant control out of the picture: once vetting is
 * inconsistent, nothing about an invitation changes the outcome, so the
 * invitation is never asked about on that path.
 */
export function induceFlow(
  ceremony: CeremonyManifest,
  rows: ProbeRow[],
): FlowNode {
  return build(rows, ceremony.fields);
}

function build(rows: ProbeRow[], fields: FieldDef[]): FlowNode {
  const endings = distinctEndings(rows);
  if (rows.length === 0) return { kind: "varies", effects: [] };
  if (endings.size === 1) {
    const v = rows[0]!.verdict;
    return { kind: "verdict", effect: v?.effect ?? "none", with: v?.with };
  }

  let best: Scored | null = null;
  for (const field of fields) {
    const groups = group(rows, field);
    if (groups.size < 2) continue;
    let impurity = 0;
    for (const bucket of groups.values()) {
      impurity += (bucket.length / rows.length) * distinctEndings(bucket).size;
    }
    const scored: Scored = { field, groups, impurity };
    if (
      !best ||
      scored.impurity < best.impurity ||
      (scored.impurity === best.impurity && scored.groups.size < best.groups.size)
    ) {
      best = scored;
    }
  }

  if (!best) {
    return {
      kind: "varies",
      effects: [...new Set(rows.map((r) => r.verdict?.effect ?? "none"))].sort(),
    };
  }

  const rest = fields.filter((f) => f.key !== best!.field.key);
  const branches: FlowBranch[] = [];
  // Branch order follows the field's declared options, not the probe order, so
  // the chart reads the way the control does.
  for (const value of optionsFor(best.field)) {
    const bucket = best.groups.get(JSON.stringify(value));
    if (!bucket || bucket.length === 0) continue;
    branches.push({
      value,
      label: labelFor(best.field, value),
      node: build(bucket, rest),
    });
  }

  // Every answer lands in the same place: the question does not matter here.
  const first = branches[0]?.node;
  if (
    first &&
    first.kind === "verdict" &&
    branches.every(
      (b) =>
        b.node.kind === "verdict" &&
        verdictKey(b.node as Verdict) === verdictKey(first as Verdict),
    )
  ) {
    return first;
  }

  return {
    kind: "ask",
    field: best.field.key,
    label: best.field.label,
    hint: best.field.hint,
    branches,
  };
}

/** The path a given set of inputs takes, as the indexes of the branches taken. */
export function tracePath(node: FlowNode, values: FieldValues): number[] {
  const path: number[] = [];
  let current = node;
  while (current.kind === "ask") {
    const ask = current;
    const i = ask.branches.findIndex(
      (b) => JSON.stringify(b.value) === JSON.stringify(values[ask.field]),
    );
    if (i < 0) return path;
    path.push(i);
    current = ask.branches[i]!.node;
  }
  return path;
}

/** The ending a given set of inputs reaches, or null when the path runs out. */
export function traceVerdict(
  node: FlowNode,
  values: FieldValues,
): FlowVerdict | FlowVaries | null {
  let current = node;
  while (current.kind === "ask") {
    const ask = current;
    const branch = ask.branches.find(
      (b) => JSON.stringify(b.value) === JSON.stringify(values[ask.field]),
    );
    if (!branch) return null;
    current = branch.node;
  }
  return current;
}

/** How many endings the chart has, for the surface to report. */
export function countEndings(node: FlowNode): number {
  if (node.kind !== "ask") return 1;
  return node.branches.reduce((n, b) => n + countEndings(b.node), 0);
}
